import { isEqual, isNil } from "lodash-es";
import type { FieldConfig } from "../../fields/types";
import type { FormScope, FormVersion } from "../../state/formVersion";
import SubformId from "../../state/SubformId";
import type { ExportField, ExportFieldWithFieldType } from "../../types/ExportField";
import type { FieldType } from "../../types";
import { Scope } from "../../types";
import type { IconName } from "../../components/icon/Icon";

export const mapScopedFieldsToExportField = (scopedFields: Record<string, ExportFieldWithFieldType[]>): ExportField[] =>
  mapScopedFieldsToExportFieldHelper(formScopeToString(Scope.ROOT), scopedFields);

export const mapScopedFieldsToExportFieldHelper = (
  currentScope: string,
  scopedFields: Record<string, ExportFieldWithFieldType[]>,
): ExportField[] =>
  (scopedFields[currentScope] ?? []).map((field) => {
    const fields =
      field.fieldType === "subform" && !isNil(field.subformId)
        ? mapScopedFieldsToExportFieldHelper(field.subformId.toString(), scopedFields)
        : field.fields;

    return {
      name: field.name,
      dataName: field.dataName,
      exportFieldType: field.exportFieldType,
      fields,
    };
  });

export const mapExportFieldToScopedFields = (
  exportFields: ExportField[],
  formVersion: FormVersion,
): Record<string, ExportFieldWithFieldType[]> =>
  exportFields.reduce(
    (acc, field) => mapExportFieldHelper(field, Scope.ROOT, formVersion, acc),
    {} as Record<string, ExportFieldWithFieldType[]>,
  );

const mapExportFieldHelper = (
  exportField: ExportField,
  currentScope: FormScope,
  formVersion: FormVersion,
  result: Record<string, ExportFieldWithFieldType[]>,
): Record<string, ExportFieldWithFieldType[]> => {
  const fieldConfig = getField(exportField.dataName, currentScope, formVersion);

  if (isNil(fieldConfig)) {
    return addToRecord(currentScope, exportField, "unknown", result);
  }

  if (fieldConfig.type === "subform" && fieldConfig.properties.target_form_id instanceof SubformId) {
    return exportField.fields.reduce(
      (acc, field) => mapExportFieldHelper(field, fieldConfig.properties.target_form_id as SubformId, formVersion, acc),
      addToRecord(currentScope, exportField, fieldConfig.type, result, fieldConfig.properties.target_form_id),
    );
  } else if (fieldConfig.type === "pin") {
    const newScope = SubformId.from(fieldConfig.properties.target_form_id as string);
    return exportField.fields.reduce((acc, field) => mapExportFieldHelper(field, newScope, formVersion, acc), result);
  }

  return addToRecord(currentScope, exportField, fieldConfig.type, result);
};

const addToRecord = (
  scope: FormScope,
  exportField: ExportField,
  fieldType: FieldType,
  result: Record<string, ExportFieldWithFieldType[]>,
  subformId?: SubformId,
): Record<string, ExportFieldWithFieldType[]> => {
  const stringScope = formScopeToString(scope);
  result[stringScope] ??= [];
  if (!result[stringScope].some((item) => item.dataName === exportField.dataName)) {
    result[stringScope].push({ ...exportField, fieldType, ...(!isNil(subformId) && { subformId, fields: [] }) });
  }
  return result;
};

export const getField = (dataName: string, scope: FormScope, formVersion: FormVersion): FieldConfig | undefined =>
  formVersion.fields.find((field) => field.properties.data_name === dataName && isEqual(scope, field.scope));

export const formScopeToString = (formScope: FormScope): string =>
  formScope instanceof SubformId ? formScope.toString() : "ROOT";

export const mapMetaIcons = (dataName: string): IconName => {
  switch (dataName) {
    case "meta.serialNumber":
      return "tag";
    case "meta.registrationDate":
      return "calendar";
    case "meta.summary":
      return "viewList";
    case "meta.location.latitude":
      return "gpsLocation";
    case "meta.location.longitude":
      return "gpsLocation";
    case "meta.userId":
      return "user";
    default:
      return "document";
  }
};

export const mapColumnIdsToDataNames = (columnId: string) => {
  if (columnId === "info.userId") {
    return "meta.userId";
  } else if (columnId === "info.date") {
    return "meta.registrationDate";
  } else if (columnId === "info.description") {
    return "meta.summary";
  } else if (columnId.startsWith("info.")) {
    return "meta." + columnId.substring(5);
  } else if (columnId.startsWith("data.")) {
    return columnId.substring(5);
  }

  return columnId;
};
